import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Don't auto-redirect - let the app handle auth state
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  createSession: (sessionId) => api.post('/auth/session', {}, {
    headers: { 'X-Session-ID': sessionId }
  }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

// Lands API
export const landsAPI = {
  create: (data) => api.post('/lands', data),
  getAll: () => api.get('/lands'),
  getOne: (id) => api.get(`/lands/${id}`),
  update: (id, data) => api.put(`/lands/${id}`, data),
  delete: (id) => api.delete(`/lands/${id}`),
};

// Projects API
export const projectsAPI = {
  create: (data) => api.post('/projects', data),
  getAll: () => api.get('/projects'),
  getOne: (id) => api.get(`/projects/${id}`),
  updateStage: (projectId, stageId, data) => api.put(`/projects/${projectId}/stages/${stageId}`, data),
};

// Builders API
export const buildersAPI = {
  getAll: (params) => api.get('/builders', { params }),
  getOne: (id) => api.get(`/builders/${id}`),
  getMyProfile: () => api.get('/builders/me/profile'),
  updateProfile: (data) => api.put('/builders/profile', data),
};

// Suppliers API
export const suppliersAPI = {
  getAll: (params) => api.get('/suppliers', { params }),
  getOne: (id) => api.get(`/suppliers/${id}`),
  createProfile: (data) => api.post('/suppliers/profile', data),
};

// Materials API
export const materialsAPI = {
  getAll: (params) => api.get('/materials', { params }),
  getCategories: () => api.get('/materials/categories'),
  create: (data) => api.post('/materials', data),
};

// Valuation API
export const valuationAPI = {
  estimate: (data) => api.post('/valuation/estimate', data),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Hire Requests API
export const hireRequestsAPI = {
  create: (data) => api.post('/hire-requests', data),
  getAll: (params) => api.get('/hire-requests', { params }),
  update: (id, data) => api.put(`/hire-requests/${id}`, data),
};

// Orders API
export const ordersAPI = {
  create: (data) => api.post('/orders', data),
  getAll: (params) => api.get('/orders', { params }),
  update: (id, data) => api.put(`/orders/${id}`, data),
};

// Land Documents API
export const documentsAPI = {
  upload: (landId, data) => api.post(`/lands/${landId}/documents`, data),
  getAll: (landId) => api.get(`/lands/${landId}/documents`),
  delete: (landId, documentId) => api.delete(`/lands/${landId}/documents/${documentId}`),
};

// Featured Builders API
export const featuredAPI = {
  getBuilders: () => api.get('/builders/featured'),
};

export default api;
